import { useNavigate } from "react-router-dom";
import {
  Search, ShieldCheck, Sparkles, Users, MessagesSquare, Lock, Heart,
  CheckCircle2, ArrowRight, Quote, Star, Brain, HeartHandshake,
} from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";

const HERO_IMG = "https://images.unsplash.com/photo-1630526720753-aa4e71acf67d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxoYXBweSUyMEluZGlhbiUyMGNvdXBsZSUyMHByZW1pdW0lMjBwcm9maWxlfGVufDB8fHx8MTc5MDI5MjYxNHww&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG = "https://images.unsplash.com/photo-1659352787906-f809a3b9e86e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHx0cmFkaXRpb25hbCUyMG1vZGVybiUyMEluZGlhbiUyMGZhbWlseSUyMGhhcHBpbmVzc3xlbnwwfHx8fDE3OTAyOTI2MTR8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const nav = useNavigate();
  return (
    <div className="bg-[#FDFBF7]" data-testid="home-page">
      <PublicNav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#3B0A12] text-[#FAF5F0]">
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div className="p-8 sm:p-12 lg:p-16">
              <span className="chip mb-5">India's trusted matrimonial platform</span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Find Your Jodi.<br /><span className="text-[#E2C382]">Build Your Future.</span>
              </h1>
              <p className="mt-5 text-[#E7CDCF] text-base sm:text-lg max-w-md">
                Verified profiles, AI compatibility and family connect — everything you need to find a life partner, with you in control.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button onClick={() => nav("/register")} data-testid="hero-create-profile" className="btn-gold text-base px-7 py-3.5 rounded-xl">Create Free Profile</button>
                <button onClick={() => nav("/register")} data-testid="hero-find-jodi" className="border border-[#C59B27]/60 text-[#FAF5F0] font-semibold px-7 py-3.5 rounded-xl hover:bg-[#781826] transition flex items-center justify-center gap-2">
                  <Search className="w-4 h-4" /> Find Your Jodi
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-[#D8B4B9]">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#C59B27]" /> Verified Profiles</span>
                <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#C59B27]" /> Privacy First</span>
              </div>
            </div>
            <div className="hidden lg:block relative h-full min-h-[520px]">
              <img src={HERO_IMG} alt="Happy couple" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#3B0A12] via-transparent to-transparent" />
            </div>
          </div>
        </div>

        {/* Find Your Jodi search visual */}
        <div className="relative -mt-6 mx-2 sm:mx-8 bg-white rounded-2xl shadow-2xl border border-[#C59B27]/30 p-4 sm:p-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["Gender", "Woman / Man"], ["Age", "25 – 32"], ["Location", "Bangalore"], ["Profession", "Software / IT"], ["Education", "Any"]].map(([l, v]) => (
            <div key={l} className="text-left">
              <div className="text-[10px] uppercase tracking-wider text-[#9E7B1B] font-semibold">{l}</div>
              <div className="text-sm font-medium text-[#261A1C] mt-0.5">{v}</div>
            </div>
          ))}
          <button onClick={() => nav("/register")} data-testid="hero-search-submit" className="btn-primary col-span-2 md:col-span-1 md:hidden lg:hidden">Search</button>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[["50K+", "Verified Profiles"], ["92%", "Avg. Match Relevance"], ["₹150", "Premium / month"], ["24/7", "Safety Monitoring"]].map(([n, l]) => (
          <div key={l} className="text-center">
            <div className="font-serif text-3xl sm:text-4xl font-bold text-[#781826]">{n}</div>
            <div className="text-sm text-[#605255] mt-1">{l}</div>
          </div>
        ))}
      </section>

      {/* How it works */}
      <Section id="how" eyebrow="How ManaJodi Works" title="Your journey to your Jodi, in four steps">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            [Users, "Create Your Profile", "Build a rich profile, self or family-managed, in guided steps."],
            [Search, "Discover Matches", "Smart search and preference-based matching find compatible profiles."],
            [Sparkles, "AI Compatibility", "See where you align and what to discuss — informational, never a verdict."],
            [HeartHandshake, "Connect Safely", "Express interest, chat securely and involve family when you're ready."],
          ].map(([Icon, t, d], i) => (
            <div key={t} className="mj-card p-6">
              <div className="w-11 h-11 rounded-xl bg-[#FAF3E0] flex items-center justify-center mb-4 text-[#781826]"><Icon className="w-5 h-5" /></div>
              <div className="text-xs font-bold text-[#C59B27]">STEP {i + 1}</div>
              <h3 className="font-serif text-xl font-semibold text-[#261A1C] mt-1">{t}</h3>
              <p className="text-sm text-[#605255] mt-2">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* AI + Verified split */}
      <Section id="ai" eyebrow="ManaJodi AI Match" title="Compatibility you can understand">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="mj-card p-8">
            <Brain className="w-8 h-8 text-[#781826] mb-4" />
            <h3 className="font-serif text-2xl font-semibold text-[#261A1C]">AI Matchmaking</h3>
            <p className="text-sm text-[#605255] mt-2">Our AI analyzes lifestyle, career, family expectations, location and interests to explain <em>why</em> a profile is recommended — with a clear breakdown, not an unexplained number.</p>
            <div className="mt-5 space-y-3">
              {[["Lifestyle", 94], ["Career", 91], ["Family", 90], ["Interests", 87]].map(([l, v]) => (
                <div key={l}>
                  <div className="flex justify-between text-xs font-medium text-[#605255] mb-1"><span>{l}</span><span className="text-[#781826] font-semibold">{v}%</span></div>
                  <div className="h-2 rounded-full bg-[#F1E9DA]"><div className="h-full rounded-full" style={{ width: `${v}%`, background: "linear-gradient(90deg,#C59B27,#781826)" }} /></div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#9E7B1B] mt-4">Compatibility is informational and does not predict marriage success.</p>
          </div>
          <div className="grid gap-6">
            {[
              [ShieldCheck, "Verified Profiles", "Mobile, email, photo, ID, education and employment verification — badges appear only when checks are actually completed."],
              [Sparkles, "Compatibility Lab", "A deep questionnaire reveals strong alignment, moderate alignment and topics worth discussing before you meet."],
              [Lock, "Safety & Privacy", "Granular privacy controls, private photos, block & report, and 24/7 safety monitoring keep you protected."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="mj-card p-6 flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#FAF3E0] flex items-center justify-center text-[#781826] shrink-0"><Icon className="w-5 h-5" /></div>
                <div><h3 className="font-serif text-xl font-semibold text-[#261A1C]">{t}</h3><p className="text-sm text-[#605255] mt-1">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Family connect */}
      <Section eyebrow="Family Connect" title="Bring your family into the journey">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="rounded-2xl overflow-hidden aspect-[4/3]"><img src={FAMILY_IMG} alt="Family" className="w-full h-full object-cover" /></div>
          <div>
            <p className="text-[#605255]">Invite parents or siblings with limited, controlled access. They can view permitted profiles, shortlist and add family notes — while your private conversations always stay private.</p>
            <ul className="mt-5 space-y-3">
              {["Invite father, mother, brother, sister or guardian", "You control exactly what each member sees", "Family notes & suggestions in one place", "Private chats are never shared automatically"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-[#261A1C]"><CheckCircle2 className="w-4 h-4 text-[#15803D] mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Success stories */}
      <Section id="stories" eyebrow="Success Stories" title="Journeys that began at ManaJodi">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            [" Arjun & Kavya", "Bangalore", "The compatibility breakdown gave us real things to talk about. Six months later, we're engaged."],
            ["Rohit & Sneha", "Hyderabad", "Family Connect let our parents be part of the process without taking over. It felt respectful."],
            ["Vikram & Ananya", "Pune", "Verified profiles gave us confidence. We knew we were talking to genuine people."],
          ].map(([n, c, q]) => (
            <div key={n} className="mj-card p-6">
              <Quote className="w-7 h-7 text-[#C59B27] mb-3" />
              <p className="text-sm text-[#261A1C] leading-relaxed">"{q}"</p>
              <div className="mt-4 flex items-center gap-0.5 text-[#C59B27]">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5" fill="#C59B27" />)}</div>
              <div className="mt-2 text-sm font-semibold text-[#781826] capitalize">{n} <span className="font-normal text-[#605255]">· {c}</span></div>
            </div>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-[#781826] text-center p-10 sm:p-16">
          <Heart className="w-10 h-10 text-[#E2C382] mx-auto mb-4" />
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#FAF5F0]">Your Jodi is waiting.</h2>
          <p className="text-[#E7CDCF] mt-3 max-w-lg mx-auto">Create your free profile today and discover meaningful, compatible matches.</p>
          <button onClick={() => nav("/register")} className="btn-gold mt-7 text-base px-8 py-3.5 rounded-xl inline-flex items-center gap-2">Create Free Profile <ArrowRight className="w-4 h-4" /></button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Section({ id, eyebrow, title, children }) {
  return (
    <section id={id} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 scroll-mt-20">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-wider text-[#C59B27] font-semibold">{eyebrow}</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#58111A] mt-1">{title}</h2>
      </div>
      {children}
    </section>
  );
}
